import React, { useEffect, useState } from "react";
import {
  GitBranch, Loader2, RefreshCw, Cloud, AlertCircle, CheckCircle2, ExternalLink,
  ShieldCheck, Activity, Download,
} from "lucide-react";
import {
  adminGitStatus, adminGitPull, adminGitBackupDb, adminGitListBackups,
  adminGetAuditLog,
} from "../../lib/api";

export default function OperationsPanel({ onToast }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState({});
  const [backups, setBackups] = useState([]);
  const [audit, setAudit] = useState([]);

  const refreshAll = async () => {
    try {
      const [s, b, a] = await Promise.all([
        adminGitStatus(),
        adminGitListBackups(50).catch(() => ({ items: [] })),
        adminGetAuditLog(100).catch(() => ({ items: [] })),
      ]);
      setStatus(s);
      setBackups(b.items || []);
      setAudit(a.items || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Failed to load ops data", "error");
    }
  };

  useEffect(() => { refreshAll(); }, []);

  const run = async (key, fn, msg) => {
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      const r = await fn();
      onToast?.(msg || "Done");
      await refreshAll();
      return r;
    } catch (e) {
      onToast?.(e?.response?.data?.detail || `${key} failed`, "error");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  return (
    <div data-testid="operations-panel">
      <h2 className="lm-font font-extrabold text-[#11151C] text-2xl flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-[#FF6B3D]" /> Operations
      </h2>
      <p className="text-[#11151C]/60 text-sm mt-1 max-w-2xl">
        Code deployment, database backups to GitHub, and an audit trail of every admin action.
      </p>

      {/* Git / deploy */}
      <div className="border border-[#11151C]/10 rounded-2xl p-5 mt-5 bg-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-bold text-[#11151C] inline-flex items-center gap-2"><GitBranch className="w-5 h-5 text-[#FF6B3D]" /> Source control</h3>
          <button onClick={refreshAll} className="text-xs font-semibold text-[#11151C]/70 inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        {status ? (
          <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <Kv label="Branch" value={status.branch || "—"} />
            <Kv label="Commit" value={status.commit || "—"} />
            <Kv label="Last commit" value={status.last_commit || "—"} mono />
            <Kv label="Working tree" value={status.dirty ? "Uncommitted changes" : "Clean"} bad={status.dirty} />
          </div>
        ) : (
          <div className="py-6 text-center text-[#11151C]/50"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => run("pull", () => adminGitPull(true), "Code pulled + services restarted")}
            disabled={busy.pull}
            data-testid="git-pull-btn"
            className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-60"
          >
            {busy.pull ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Pull latest code + restart
          </button>
          {status?.dirty && (
            <div className="text-xs text-amber-700 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5" /> Working tree has uncommitted changes — pull may fail (use a clean tree on your VPS).
            </div>
          )}
        </div>
      </div>

      {/* Backup */}
      <div className="border border-[#11151C]/10 rounded-2xl p-5 mt-5 bg-white">
        <h3 className="font-bold text-[#11151C] inline-flex items-center gap-2"><Cloud className="w-5 h-5 text-[#FF6B3D]" /> Database backup → GitHub</h3>
        {!status?.backup_configured && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl p-3 inline-flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              Set <code>GITHUB_TOKEN</code> + <code>GITHUB_BACKUP_REPO</code> in <code>backend/.env</code> to enable.
              Use a fine-grained PAT scoped to the backup repo with <strong>contents: read &amp; write</strong>.
            </div>
          </div>
        )}
        <p className="text-xs text-[#11151C]/60 mt-2 max-w-2xl">
          Runs <code>mongodump</code> (or a JSON fallback), tars + gzips the whole DB, and commits it as a single file
          to your configured backup repo's <code>backups</code> branch. Safe to run on demand or from cron.
        </p>
        <button
          onClick={() => run("backup", adminGitBackupDb, "Database backed up to GitHub")}
          disabled={busy.backup || !status?.backup_configured}
          data-testid="git-backup-btn"
          className="lm-btn-primary mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-60"
        >
          {busy.backup ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
          Backup database to GitHub
        </button>

        {backups.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/60 mb-1.5">Recent backups</div>
            <div className="border border-[#11151C]/10 rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-[#FFFBF6]">
                  <tr><th className="text-left p-2.5">When</th><th className="text-left p-2.5">Path</th><th className="text-left p-2.5">Size</th><th className="text-right p-2.5">Open</th></tr>
                </thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.commit_sha || b.path || b.ts} className="border-t border-[#11151C]/5">
                      <td className="p-2.5">{new Date(b.ts).toLocaleString()}</td>
                      <td className="p-2.5 font-mono">{b.path}</td>
                      <td className="p-2.5">{Math.round((b.size_bytes || 0) / 1024)} KB</td>
                      <td className="p-2.5 text-right">
                        {b.html_url && <a href={b.html_url} target="_blank" rel="noopener noreferrer" className="text-[#FF6B3D] inline-flex items-center gap-1 font-semibold">View <ExternalLink className="w-3 h-3" /></a>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Audit log */}
      <div className="border border-[#11151C]/10 rounded-2xl p-5 mt-5 bg-white">
        <h3 className="font-bold text-[#11151C] inline-flex items-center gap-2"><Activity className="w-5 h-5 text-[#FF6B3D]" /> Audit log</h3>
        <p className="text-xs text-[#11151C]/60 mt-1">
          Every POST/PUT/PATCH/DELETE on <code>/api/admin/**</code> is recorded automatically.
        </p>
        {audit.length === 0 ? (
          <div className="py-8 text-center text-[#11151C]/40 text-sm">
            <CheckCircle2 className="w-5 h-5 mx-auto" />
            No admin mutations recorded yet.
          </div>
        ) : (
          <div className="border border-[#11151C]/10 rounded-xl overflow-hidden mt-3 max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FFFBF6] sticky top-0">
                <tr><th className="text-left p-2.5">When</th><th className="text-left p-2.5">Actor</th><th className="text-left p-2.5">Method</th><th className="text-left p-2.5">Path</th><th className="text-left p-2.5">Status</th></tr>
              </thead>
              <tbody>
                {audit.map((a) => (
                  <tr key={`${a.ts}-${a.path}-${a.actor_email || "anon"}`} className="border-t border-[#11151C]/5">
                    <td className="p-2.5 whitespace-nowrap">{new Date(a.ts).toLocaleString()}</td>
                    <td className="p-2.5">{a.actor_email || <span className="text-[#11151C]/40">anonymous</span>}</td>
                    <td className="p-2.5 font-mono font-bold">{a.action}</td>
                    <td className="p-2.5 font-mono">{a.path}</td>
                    <td className="p-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${a.status < 300 ? "bg-emerald-100 text-emerald-700" : a.status < 400 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{a.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value, mono, bad }) {
  return (
    <div className="bg-[#FFFBF6] border border-[#FF6B3D]/15 rounded-xl px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-[#11151C]/55">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : "font-semibold"} ${bad ? "text-red-600" : "text-[#11151C]"}`}>{value}</div>
    </div>
  );
}
